import { Fragment } from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";
import { METRICS_COLUMNS, metricsColumn } from "../metricsColumns";
import { MetricsCellContent } from "./MetricsCellContent";
import type { PartnerMetricsRow } from "../metrics.types";

interface PartnerMetricsTileProps {
  row: PartnerMetricsRow;
  onSelectPartner: (id: string) => void;
}

/**
 * How to read one METRICS_COLUMNS field's value off a row — "name" and
 * "status" aren't here, they're the card header instead of a body row.
 * Reading labels + the column set from METRICS_COLUMNS (rather than a
 * second hardcoded list) means a column added/renamed/reordered there can't
 * silently drift out of sync with what the tile shows.
 */
const BODY_COLUMNS = METRICS_COLUMNS.filter((column) => column.tileSlot === "body");

/**
 * One partner as a card — the mobile alternative to a `PartnerMetricsTable`
 * row. Same fields as `METRICS_COLUMNS`, same status-dot treatment as the
 * table (`StatusDot`, shared), just stacked instead of columned. Tapping the
 * card selects that partner, same as clicking a table row on desktop.
 */
export function PartnerMetricsTile({ row, onSelectPartner }: PartnerMetricsTileProps) {
  return (
    <Card
      variant="outlined"
      onClick={() => {
        if (window.getSelection()?.toString()) return;
        onSelectPartner(row.id);
      }}
      sx={{ cursor: "pointer" }}
    >
      <CardContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 1,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            <MetricsCellContent cell={metricsColumn("name").project(row)} />
          </Typography>
          <Typography variant="body2">
            <MetricsCellContent cell={metricsColumn("status").project(row)} />
          </Typography>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", rowGap: 0.5, columnGap: 1.5 }}>
          {BODY_COLUMNS.map((column) => (
            <Fragment key={column.id}>
              <Typography variant="body2" color="text.secondary">
                {column.label}
              </Typography>
              <Typography variant="body2" textAlign="right">
                <MetricsCellContent cell={column.project(row)} />
              </Typography>
            </Fragment>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}
